import { Flex } from 'antd';
import { useMemo, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './header.module.scss';
import type { UserInfo } from '@shared/api';
import { OrganizationPopover } from '../organization/organization-popover';
import { useAppDispatch, useAppSelector } from '@shared/redux';
import { authActions, authSelectors } from '@shared/redux/ducks/auth';
import { router } from '@app/providers/router/router-provider';
import { Routes } from '@shared/constants/routes';
import { getDefaultOrganizationRoute } from '@shared/lib/permissions';

interface HeaderProps {
  userInfo?: UserInfo | null;
}

export const Header: FC<HeaderProps> = ({ userInfo }) => {
  const { t } = useTranslation('translation', { keyPrefix: 'common.header' });
  const dispatch = useAppDispatch();

  const userOrganizations = userInfo?.organizations;
  const currentUserOrganizationId = useAppSelector(authSelectors.selectCurrentOrganizationId);
  const selectedOrganizationTitle = useAppSelector(
    authSelectors.selectCurrentOrganizationInfo,
  ).title;
  const currentOrganization = useAppSelector(authSelectors.selectCurrentOrganizationInfo);

  const userNameSplitted = userInfo?.full_name?.split(' ');

  const defaultUserTitle =
    `${userNameSplitted?.[0]?.[0] || ''}${userNameSplitted?.[1]?.[0] || ''}` ||
    t('defaultUserTitle');

  const handleSelectOrganization = (selectedOrganizationId: number) => {
    const selectedOrganization = userInfo?.organizations?.find(
      (organization) => organization.id === selectedOrganizationId,
    );

    dispatch(authActions.setCurrentOrganizationInfo(selectedOrganization));

    router.navigate(
      getDefaultOrganizationRoute(selectedOrganization?.permission, selectedOrganizationId),
    );
  };

  const handleSelectCreateOrganization = () => {
    dispatch(authActions.setCurrentOrganizationInfo(undefined));
    router.navigate(Routes.profile.organization.root);
  };

  const options = useMemo(() => {
    const list =
      userOrganizations
        ?.filter((c): c is Required<typeof c> => Boolean(c && c.id && c.title))
        .map((organization) => ({
          label: organization.title.trim(),
          value: organization.id,
          isChecked: organization.id === currentUserOrganizationId,
          permission: organization.permission,
        })) ?? [];

    const deduplicated = Array.from(new Map(list.map((c) => [c.value, c])).values());

    return deduplicated;
  }, [userOrganizations, currentUserOrganizationId]);

  return (
    <Flex justify="end" className={styles.root}>
      <Flex vertical align="end" gap={8} className={styles.wrapper}>
        <OrganizationPopover
          defaultUserTitle={defaultUserTitle.trim().toLocaleUpperCase()}
          defaultFullUserTitle={userInfo?.full_name || t('defaultFullUserTitle')}
          defaultOrganizationTitle={selectedOrganizationTitle || t('defaultOrganizationTitle')}
          options={options}
          permission={currentOrganization.permission}
          onSelect={handleSelectOrganization}
          onSelectCreateOrganization={handleSelectCreateOrganization}
        />
      </Flex>
    </Flex>
  );
};
